"use client";

import { useState, type ReactNode } from "react";
import { CheckCircle2 } from "lucide-react";
import { useForm } from "@formspree/react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { t } from "@/lib/localization";

export default function ContactFormDialog({ trigger }: { trigger?: ReactNode }) {
    const [state, handleSubmit] = useForm("xvzvnpqz");
    const [open, setOpen] = useState(false);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger ?? (
                    <button className="mt-1 text-sm text-red-600 underline hover:text-red-800">
                        {t("platform.sidebar.contact-us.trigger")}
                    </button>
                )}
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>
                        {t("platform.sidebar.contact-us.title")}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {t("platform.sidebar.contact-us.description")}
                    </p>
                </DialogHeader>
                {state.succeeded ? (
                    <div className="flex flex-col items-center gap-3 py-6">
                        <CheckCircle2 className="size-12 text-green-500" />
                        <p className="text-sm text-muted-foreground">
                            {t("platform.sidebar.contact-us.success")}
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-4"
                    >
                        <div className="space-y-2">
                            <Label htmlFor="name">
                                {t("platform.sidebar.contact-us.name")}
                            </Label>
                            <Input id="name" name="name" required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">
                                {t("platform.sidebar.contact-us.email")}
                            </Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phone">
                                {t("platform.sidebar.contact-us.phone")}
                            </Label>
                            <Input id="phone" name="phone" type="tel" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="message">
                                {t("platform.sidebar.contact-us.message")}
                            </Label>
                            <Textarea
                                id="message"
                                name="message"
                                required
                                rows={4}
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={state.submitting}
                            className="mt-2"
                        >
                            {t("platform.sidebar.contact-us.submit")}
                        </Button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
